import React, { PropsWithChildren } from 'react';

import { SelectOption } from './types';

export const MIN_OPTIONS_TO_DISPLAY_SELECT_ALL = 7.5;

type GetSelectedValueParams<T extends SelectOption> = {
  multiple: boolean;
  option: T;
  selectedOptions: T[];
  selectedOptionsWithValuesOnly: Array<string | number>;
};

export const searchSubstring = (string = '', substring = '') => {
  return string.toLowerCase().indexOf(substring.toLowerCase());
};

export function getSelectedValues<T extends SelectOption>({
  multiple,
  option,
  selectedOptions,
  selectedOptionsWithValuesOnly,
}: GetSelectedValueParams<T>): T[] {
  if (!multiple) {
    return [option];
  }

  if (selectedOptionsWithValuesOnly.includes(option.value)) {
    return selectedOptions.filter(
      (selectedOption: T) => selectedOption.value !== option.value,
    );
  }
  return [...selectedOptions, option];
}

/**
 * Returns options that are pre-selected and disabled.
 * @param allOptions
 * @param preSelectedOptions
 */
export function getFixedOptions<T extends SelectOption>(
  allOptions: T[],
  preSelectedOptions: T[],
): T[] {
  const allDisabled = allOptions.filter((option: T) => option.disabled);
  return allDisabled.filter((disabledOption: T) => {
    return preSelectedOptions.some(
      (option: T) => option.value === disabledOption.value,
    );
  });
}

/**
 * Returns a list of option that are fixed and are enabled
 * @param options
 * @param preSelectedOptions
 */
export function getAllSelectableOptions<T extends SelectOption>(
  options: T[],
  preSelectedOptions: T[],
): T[] {
  const allFixed = getFixedOptions(options, preSelectedOptions);
  const allEnabled = options.filter((option: T) => !option.disabled);
  return [...allFixed, ...allEnabled];
}

type ApplyRecursiveCallback<P> = (
  child: React.ReactElement<PropsWithChildren<P>>,
) => void;

export function applyProps<P>(
  children: React.ReactNode,
  cb: ApplyRecursiveCallback<P>,
): React.ReactNode {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return React.Children.map(children, (c) => {
    if (!React.isValidElement(c)) {
      return c;
    }
    const child = c as React.ReactElement<PropsWithChildren<P>>;
    const grandChildren: React.ReactNode | null = child.props.children;
    if (grandChildren) {
      const cloned = React.cloneElement<any>(child, {
        children: applyProps(grandChildren, cb),
      });
      return cb(cloned);
    }
    return cb(child);
  });
}
type ExtractPropsPredicate = (child: React.ReactElement) => boolean;

export function extractProps<P>(
  children: React.ReactNode,
  predicate: ExtractPropsPredicate,
): Array<P> {
  let props: Array<P> = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) {
      return;
    }
    const grandChildren: React.ReactNode | null = child.props.children;
    if (grandChildren) {
      const grandProps = extractProps<P>(grandChildren, predicate);
      props = [...props, ...grandProps];
    }
    if (predicate(child)) {
      props.push(child.props);
    }
  });
  return props;
}
